import {
  BUYER_INFO_EXTRATION_MODE,
  CONFIRM_SALE_MODE,
  PAYMENT_MODE,
  SUCCESSFUL_PAYMENT,
  WAITING_CONFIRMATION_MODE,
} from '@src/components/Checkout/constants';
import { OrderStatus, type Order } from '@src/features/orders/types';
import { SaleChangeStatusEnum, type SaleChange } from '@src/domains/sales';
import { SaleState, type Sale } from '@src/core/buyerApiClient';
import { CheckoutMode } from '../types';

type DecideCheckoutModeParams = {
  order?: Order | null;
  sale?: Sale | null;
  saleChange?: SaleChange | null;
  pendingFormInputs?: boolean;
};

type DecideCheckoutModeAfterAuthParams = DecideCheckoutModeParams & {
  skipConfirmation?: boolean;
};

export type CheckoutModeDecision = {
  mode: CheckoutMode;
  /** Set when the mode comes from a sale change that has just been applied. */
  appliedSaleChangeId?: string;
  /** Set when the mode comes from an order that is already confirmed. */
  completedOrderId?: string;
};

export const decideCheckoutMode = (params: DecideCheckoutModeParams): CheckoutModeDecision | null => {
  const { order, sale, saleChange, pendingFormInputs = false } = params;

  if (saleChange) {
    if (saleChange.status === SaleChangeStatusEnum.Pending) {
      return { mode: PAYMENT_MODE };
    }

    if (saleChange.status === SaleChangeStatusEnum.Applied) {
      return { mode: SUCCESSFUL_PAYMENT, appliedSaleChangeId: saleChange.id };
    }

    return null;
  }

  if (!order) {
    // A sale-type direct link has no order: the sale itself may be waiting on the supplier.
    // Known gap: pendingFormInputs is never checked here, so a link sale with required form inputs
    // still pending goes to payment (decideCheckoutModeAfterAuth with skipConfirmation) and skips
    // the buyer-info step. A signed-in buyer has the inputs loaded by takeConfirmedSale; a guest
    // doesn't even get them loaded after signing in.
    return sale?.state === SaleState.AwaitingConfirmation ? { mode: WAITING_CONFIRMATION_MODE } : null;
  }

  // Nothing else can happen until the supplier confirms availability — not even the additional info.
  if (order.status === OrderStatus.AwaitingConfirmation) {
    return { mode: WAITING_CONFIRMATION_MODE };
  }

  if (pendingFormInputs) {
    return { mode: BUYER_INFO_EXTRATION_MODE };
  }

  if (order.status === OrderStatus.Confirmed) {
    return { mode: SUCCESSFUL_PAYMENT, completedOrderId: order.id };
  }

  return { mode: PAYMENT_MODE };
};

export const decideCheckoutModeAfterAuth = (params: DecideCheckoutModeAfterAuthParams): CheckoutModeDecision => {
  const { skipConfirmation = false, ...purchase } = params;

  return decideCheckoutMode(purchase) ?? { mode: skipConfirmation ? PAYMENT_MODE : CONFIRM_SALE_MODE };
};
